import { useEffect, useState, useCallback } from "react";
import { apiFetch } from "../context/AuthContext";
import { fmtDateTime } from "../utils/time";

const B    = "#2C1A0E";
const ROSE = "#C9847A";
const META_BLUE = "#1877F2";

const fmt  = (n) => n == null ? "—" : Number(n).toLocaleString("en-IN");
const fmtR = (n) => n == null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`;
const fmtP = (n) => n == null ? "—" : `${Number(n).toFixed(1)}%`;
const fmtX = (n) => n == null ? "—" : `${Number(n).toFixed(2)}x`;

const ROAS_COLOR = (v) =>
  v >= 3 ? "#15803D" : v >= 2 ? "#92400E" : v > 0 ? "#DC2626" : "#aaa";

export default function MetaPage() {
  const [tab,       setTab]       = useState("campaigns");
  const [status,    setStatus]    = useState(null);
  const [remote,    setRemote]    = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [monthly,   setMonthly]   = useState([]);
  const [adsets,    setAdsets]    = useState([]);
  const [syncing,   setSyncing]   = useState(false);
  const [syncLog,   setSyncLog]   = useState([]);
  const [expanded,  setExpanded]  = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPlan,    setAiPlan]    = useState("");

  const load = useCallback(async () => {
    const s = await apiFetch("/api/meta/status");
    if (s.ok) setStatus(await s.json());
    const r = await apiFetch("/api/meta/remote-status");
    if (r.ok) setRemote(await r.json());
    const c = await apiFetch("/api/meta/campaigns");
    if (c.ok) setCampaigns(await c.json());
    const m = await apiFetch("/api/meta/monthly");
    if (m.ok) setMonthly(await m.json());
    const a = await apiFetch("/api/meta/adsets");
    if (a.ok) setAdsets(await a.json());
  }, []);

  useEffect(() => { load(); }, []);

  const runSync = async () => {
    setSyncing(true); setSyncLog([]);
    const r = await apiFetch("/api/meta/sync", { method: "POST" });
    const d = await r.json();
    setSyncLog(d.log || [d.error || "Done"]);
    setSyncing(false);
    load();
  };

  const getAiPlan = async () => {
    setAiLoading(true); setAiPlan("");
    const payload = {
      campaigns: campaigns.map(c => ({
        name: c.name, status: c.status, objective: c.objective,
        spend: c.spend, impressions: c.impressions, clicks: c.clicks,
        ctr: c.ctr, cpc: c.cpc, cpm: c.cpm, frequency: c.frequency,
        purchases: c.purchases, purchase_value: c.purchase_value,
        meta_roas: c.meta_roas,
        adsets: (c.adsets || []).map(a => ({
          name: a.name, status: a.status,
          age: `${a.age_min || "?"}–${a.age_max || "?"}`,
          genders: a.genders, interests: a.interests,
          placements: a.placements, cities: a.cities }))
      })),
      monthly };

    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          system: `You are a performance marketing expert for Embroideary, a small Indian handmade embroidery brand based in Bangalore run by a solo founder. 
Products: tote bags, pendants, phone covers, diaries, shirts, magnets, NFC cards, kids frames — priced ₹299–₹2500. 
Target customers: urban Indian women 20–35, gifting occasions, Instagram-first brand.
Industry benchmark: ROAS 2–4x is healthy for Indian D2C on Meta. Below 2x = unprofitable. Above 4x = excellent.
Be specific, actionable, and concise. Format with clear sections. Address each campaign by name. Currency is INR.`,
          messages: [{
            role: "user",
            content: `Here is our Meta ads data:\n${JSON.stringify(payload, null, 2)}\n\nGive me:\n1. Overall assessment (2-3 sentences)\n2. Campaign-by-campaign breakdown: what's working, what's not, why\n3. Audience/targeting observations\n4. Top 3 priority actions to improve ROAS\n5. Budget reallocation recommendation`
          }]
        })
      });
      const d = await res.json();
      setAiPlan(d.content?.[0]?.text || "No response");
    } catch (e) {
      setAiPlan(`Error: ${e.message}`);
    }
    setAiLoading(false);
  };

  const configured = status?.configured;

  const totalSpend    = campaigns.reduce((s, c) => s + (c.spend || 0), 0);
  const totalPurchVal = campaigns.reduce((s, c) => s + (c.purchase_value || 0), 0);
  const totalPurch    = campaigns.reduce((s, c) => s + (c.purchases || 0), 0);
  const blendedRoas   = totalSpend > 0 ? totalPurchVal / totalSpend : 0;

  return (
    <div style={{ padding: "28px 28px 80px", fontFamily: "Inter, sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "'Cormorant Garamond',Georgia,serif",
                       color: B, fontSize: 26, fontWeight: 700, margin: 0 }}>
            📣 Meta Ads
          </h1>
          <p style={{ color: "#888", fontSize: 13, marginTop: 4 }}>
            Instagram & Facebook campaigns · audience targeting · ROAS vs Shopify
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {status && (
            <div style={{ fontSize: 12, color: "#888", textAlign: "right" }}>
              {configured ? (
                <>
                  <span style={{ color: "#15803D" }}>● Connected</span> · {status.account?.name}<br/>
                  Last sync: {fmtDateTime(status.last_sync_at)}<br/>
                  {remote?.ok && (
                    <span style={{ fontSize:11, color:"#888" }}>
                      Live: {remote.campaigns ?? "—"} campaigns · {remote.adsets ?? "—"} ad sets
                    </span>
                  )}
                </>
              ) : <span style={{ color: "#DC2626" }}>● Not connected</span>}
            </div>
          )}
          {configured && (
            <button onClick={runSync} disabled={syncing}
              style={{ background: syncing ? "#aaa" : META_BLUE, color: "#fff",
                       border: "none", borderRadius: 7, padding: "8px 16px",
                       fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              {syncing ? "⏳ Syncing…" : "🔄 Sync"}
            </button>
          )}
        </div>
      </div>

      {/* Not configured */}
      {!configured && status && (
        <div style={{ background: "#FEF3EE", border: "1px solid #F9C49A",
                      borderRadius: 10, padding: "20px 24px" }}>
          <div style={{ fontWeight: 700, color: B, marginBottom: 8 }}>
            {status.has_creds ? "⚠️ Credentials found but connection failed" : "🔴 Not Connected"}
          </div>
          {status.conn_error
            ? <div style={{ fontFamily: "monospace", fontSize: 12, color: "#7A3B0E",
                            wordBreak: "break-all" }}>{status.conn_error}</div>
            : <div style={{ fontSize: 13, color: "#7A5C00" }}>
                Add <code style={cs}>META_ACCESS_TOKEN</code> and <code style={cs}>META_AD_ACCOUNT_ID</code> to Railway.
              </div>}
        </div>
      )}

      {/* Sync log */}
      {syncLog.length > 0 && <SyncLog lines={syncLog} />}

      {configured && campaigns.length > 0 && (
        <>
          {/* KPI strip */}
          <div style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
            {[
              { label: "Total Spend",        value: fmtR(totalSpend) },
              { label: "Meta-attributed Rev",value: fmtR(totalPurchVal) },
              { label: "Meta ROAS",           value: fmtX(blendedRoas),
                color: ROAS_COLOR(blendedRoas) },
              { label: "Purchases (Meta)",   value: fmt(totalPurch) },
              { label: "Active Campaigns",    value: campaigns.filter(c => (c.effective_status || c.status) === "ACTIVE").length },
            ].map(({ label, value, color }) => (
              <div key={label} style={kpiCard}>
                <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: color || B }}>{value}</div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", gap: 0, marginBottom: 20, borderBottom: "2px solid #EDE0D4" }}>
            {[
              ["campaigns", "Campaigns"],
              ["audience",  "Audience & Targeting"],
              ["monthly",   "Monthly ROAS"],
              ["ai",        "✨ AI Action Plan"],
            ].map(([id, label]) => (
              <button key={id} onClick={() => setTab(id)} style={{
                background: "none", border: "none", padding: "8px 20px",
                fontSize: 13, fontWeight: tab === id ? 700 : 400,
                color: tab === id ? B : "#888",
                borderBottom: tab === id ? `2px solid ${B}` : "2px solid transparent",
                marginBottom: -2, cursor: "pointer" }}>{label}</button>
            ))}
          </div>

          {/* ── Campaigns tab ── */}
          {tab === "campaigns" && (
            <div>
              <table style={tbl}>
                <thead>
                  <tr style={{ background: "#F7F5F2" }}>
                    {["Campaign","Status","Spend","Impressions","Clicks","CTR","CPM","Frequency","Purchases","Meta ROAS"].map(h => (
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map(c => (
                    <>
                      <tr key={c.campaign_id}
                        onClick={() => setExpanded(expanded === c.campaign_id ? null : c.campaign_id)}
                        style={{ cursor: "pointer", background: expanded === c.campaign_id ? "#FDF8F5" : "#fff" }}>
                        <td style={{ ...td, fontWeight: 600, color: B }}>
                          <span style={{ marginRight: 6, fontSize: 10 }}>
                            {expanded === c.campaign_id ? "▼" : "▶"}
                          </span>
                          {c.name}
                        </td>
                        <td style={td}>
                          {(() => {
                            const eff = c.effective_status || c.status;
                            const isActive = eff === "ACTIVE";
                            const isBoosted = c.objective === "POST_ENGAGEMENT" || c.objective === "OUTCOME_ENGAGEMENT";
                            return (
                              <span style={{
                                background: isActive ? "#DCFCE7" : eff === "ARCHIVED" ? "#FEF9C3" : "#F3F4F6",
                                color: isActive ? "#15803D" : eff === "ARCHIVED" ? "#854D0E" : "#6B7280",
                                borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
                                {isBoosted ? "📢 " : ""}{eff}
                              </span>
                            );
                          })()}
                        </td>
                        <td style={tdr}>{fmtR(c.spend)}</td>
                        <td style={tdr}>{fmt(c.impressions)}</td>
                        <td style={tdr}>{fmt(c.clicks)}</td>
                        <td style={tdr}>{fmtP(c.ctr)}</td>
                        <td style={tdr}>{fmtR(c.cpm)}</td>
                        <td style={tdr}>{c.frequency?.toFixed(1) || "—"}</td>
                        <td style={tdr}>{fmt(c.purchases)}</td>
                        <td style={{ ...tdr, fontWeight: 700, color: ROAS_COLOR(c.meta_roas) }}>
                          {fmtX(c.meta_roas)}
                        </td>
                      </tr>
                      {expanded === c.campaign_id && (c.adsets || []).length > 0 && (
                        <tr key={`${c.campaign_id}-adsets`}>
                          <td colSpan={10} style={{ padding: "0 0 8px 24px", background: "#FDF8F5" }}>
                            <div style={{ fontSize: 11, color: "#888", fontWeight: 700,
                                          textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
                              Ad Sets
                            </div>
                            {(c.adsets || []).map(a => (
                              <div key={a.adset_id} style={{
                                background: "#fff", border: "1px solid #EDE0D4",
                                borderRadius: 8, padding: "10px 14px", marginBottom: 8, fontSize: 12 }}>
                                <div style={{ fontWeight: 700, color: B, marginBottom: 6 }}>
                                  {a.name}
                                  <span style={{
                                    marginLeft: 10, fontSize: 10, fontWeight: 600,
                                    color: a.status === "ACTIVE" ? "#15803D" : "#6B7280"
                                  }}>{a.status}</span>
                                </div>
                                <div style={{ display: "flex", gap: 20, flexWrap: "wrap", color: "#555" }}>
                                  <span>🎯 <strong>Age:</strong> {a.age_min}–{a.age_max}</span>
                                  <span>⚧ <strong>Gender:</strong> {(a.genders || []).join(", ") || "All"}</span>
                                  <span>📍 <strong>Cities:</strong> {(a.cities || []).join(", ") || (a.countries || []).join(", ") || "All"}</span>
                                  <span>📱 <strong>Placements:</strong> {(a.placements || []).join(", ") || "All"}</span>
                                  {a.instagram_positions?.length > 0 &&
                                    <span>📸 <strong>IG:</strong> {a.instagram_positions.join(", ")}</span>}
                                </div>
                                {(a.interests || []).length > 0 && (
                                  <div style={{ marginTop: 6 }}>
                                    <strong>Interests:</strong>{" "}
                                    {a.interests.map(i => (
                                      <span key={i} style={{
                                        background: "#F0EBE3", borderRadius: 4,
                                        padding: "1px 7px", marginRight: 4, fontSize: 11 }}>{i}</span>
                                    ))}
                                  </div>
                                )}
                                {(a.custom_audiences || []).length > 0 && (
                                  <div style={{ marginTop: 6 }}>
                                    <strong>Custom Audiences:</strong>{" "}
                                    {a.custom_audiences.map(i => (
                                      <span key={i} style={{
                                        background: "#EBF5FB", borderRadius: 4,
                                        padding: "1px 7px", marginRight: 4, fontSize: 11, color: "#1a5276" }}>{i}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Audience tab ── */}
          {tab === "audience" && (
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              {adsets.map(a => (
                <div key={a.adset_id} style={{ ...card, flex: "1 1 300px" }}>
                  <div style={{ fontWeight: 700, color: B, fontSize: 14, marginBottom: 4 }}>{a.name}</div>
                  <div style={{ fontSize: 11, color: a.status === "ACTIVE" ? "#15803D" : "#888",
                                fontWeight: 600, marginBottom: 10 }}>{a.status}</div>
                  <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                    {[
                      ["Age",        `${a.age_min || "?"}–${a.age_max || "?"}`],
                      ["Gender",     (a.genders || []).join(", ") || "All"],
                      ["Locations",  [...(a.cities || []), ...(a.countries || [])].join(", ") || "All"],
                      ["Placements", (a.placements || []).join(", ") || "All"],
                      ["IG position",(a.instagram_positions || []).join(", ") || "All"],
                      ["Goal",       a.optimization_goal],
                    ].map(([k, v]) => v && (
                      <tr key={k}>
                        <td style={{ color: "#888", paddingBottom: 5, paddingRight: 12, verticalAlign: "top" }}>{k}</td>
                        <td style={{ fontWeight: 500, color: "#333", paddingBottom: 5 }}>{v}</td>
                      </tr>
                    ))}
                  </table>
                  {(a.interests || []).length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>Interests</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {a.interests.map(i => (
                          <span key={i} style={{ background: "#F0EBE3", borderRadius: 4,
                                                  padding: "2px 8px", fontSize: 11 }}>{i}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {(a.custom_audiences || []).length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>Custom Audiences</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {a.custom_audiences.map(i => (
                          <span key={i} style={{ background: "#EBF5FB", color: "#1a5276",
                                                  borderRadius: 4, padding: "2px 8px", fontSize: 11 }}>{i}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── Monthly ROAS tab ── */}
          {tab === "monthly" && (
            <div>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 14, lineHeight: 1.6 }}>
                <strong>Meta ROAS</strong> = Meta-attributed purchases ÷ ad spend (Meta claims any purchase within 7 days of a click).<br/>
                <strong>Blended ROAS</strong> = Total Shopify B2C revenue ÷ ad spend — more conservative and honest.
              </div>
              <table style={tbl}>
                <thead>
                  <tr style={{ background: "#F7F5F2" }}>
                    {["Month","Ad Spend","Meta Rev","Meta ROAS","Shopify B2C Rev","Blended ROAS","Impressions","Clicks","CTR","Purchases"].map(h => (
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {monthly.map(m => (
                    <tr key={m.month} style={{ borderBottom: "1px solid #F0E8E0" }}>
                      <td style={{ ...td, fontWeight: 600 }}>{m.month}</td>
                      <td style={tdr}>{fmtR(m.spend)}</td>
                      <td style={tdr}>{fmtR(m.purchase_value)}</td>
                      <td style={{ ...tdr, fontWeight: 700, color: ROAS_COLOR(m.meta_roas) }}>
                        {fmtX(m.meta_roas)}
                      </td>
                      <td style={tdr}>{fmtR(m.shopify_revenue)}</td>
                      <td style={{ ...tdr, fontWeight: 700, color: ROAS_COLOR(m.blended_roas) }}>
                        {fmtX(m.blended_roas)}
                      </td>
                      <td style={tdr}>{fmt(m.impressions)}</td>
                      <td style={tdr}>{fmt(m.clicks)}</td>
                      <td style={tdr}>{fmtP(m.ctr)}</td>
                      <td style={tdr}>{fmt(m.purchases)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── AI Action Plan tab ── */}
          {tab === "ai" && (
            <div style={{ }}>
              <div style={{ fontSize: 13, color: "#666", marginBottom: 16, lineHeight: 1.6 }}>
                Claude analyses your live campaign data — spend, ROAS, targeting, frequency —
                and gives you a specific action plan for Embroideary.
              </div>
              {!aiPlan && (
                <button onClick={getAiPlan} disabled={aiLoading}
                  style={{ background: aiLoading ? "#aaa" : B, color: "#FAF7F2",
                           border: "none", borderRadius: 8, padding: "11px 24px",
                           fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                  {aiLoading ? "⏳ Analysing campaigns…" : "✨ Generate Action Plan"}
                </button>
              )}
              {aiPlan && (
                <>
                  <div style={{ background: "#FDF8F5", border: "1px solid #EDE0D4",
                                borderRadius: 10, padding: "20px 24px",
                                fontSize: 13, lineHeight: 1.85, color: "#333",
                                whiteSpace: "pre-wrap" }}>
                    {aiPlan}
                  </div>
                  <button onClick={() => { setAiPlan(""); }}
                    style={{ marginTop: 12, background: "none", border: "1px solid #DDD4C8",
                             borderRadius: 7, padding: "6px 16px", fontSize: 12,
                             color: "#888", cursor: "pointer" }}>
                    Regenerate
                  </button>
                </>
              )}
            </div>
          )}
        </>
      )}

      {configured && campaigns.length === 0 && !syncing && (
        <div style={{ color: "#888", fontSize: 13, marginTop: 20 }}>
          No campaign data yet — run a sync first.
        </div>
      )}
    </div>
  );
}

function SyncLog({ lines }) {
  return (
    <div style={{ background: "#0D1F0D", borderRadius: 8, padding: "10px 14px",
                  maxHeight: 160, overflowY: "auto", marginBottom: 20 }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          fontSize: 12, fontFamily: "monospace", marginBottom: 3,
          color: line.startsWith("✅") ? "#7EC8A0"
               : line.startsWith("❌") ? "#E88"
               : "#ccc"
        }}>{line}</div>
      ))}
    </div>
  );
}

const card    = { background: "#fff", border: "1px solid #EDE0D4", borderRadius: 12, padding: "16px 18px" };
const kpiCard = { ...card, minWidth: 140, flex: 1 };
const tbl     = { width: "100%", borderCollapse: "collapse", fontSize: 12 };
const th      = { padding: "8px 12px", textAlign: "left", fontWeight: 600,
                  color: "#666", fontSize: 11, textTransform: "uppercase",
                  letterSpacing: 0.5, borderBottom: "2px solid #EDE0D4" };
const td      = { padding: "10px 12px", borderBottom: "1px solid #F0E8E0", verticalAlign: "top" };
const tdr     = { ...td, textAlign: "right" };
const cs      = { background: "#F0EBE3", padding: "1px 6px", borderRadius: 4,
                  fontSize: 11, fontFamily: "monospace" };
